import React, { useEffect } from "react";
import { ChipPicker } from "./ChipPicker";
import { VoiceButton } from "./VoiceButton";

export const emptyGroup = () => ({ student_ids: [], activities: [], chips: [], topic: "", homework: "", homework_items: [""], homework_due: "", test: null, level: null });

const HomeworkItems = ({ items, onChange }) => (
  <div data-testid="group-homework">
    <p className="mb-1 text-xs font-bold text-slate-700">Homework <span className="font-normal text-slate-500">— one item per line, students tick each one</span></p>
    <div className="space-y-1.5">
      {items.map((v, i) => (
        <div key={i} className="flex items-start gap-2">
          <input value={v} onChange={(e) => onChange(items.map((x, j) => (j === i ? e.target.value : x)))} placeholder={i === 0 ? "e.g. Ex 5.2 Q1–10" : "Next homework item"}
            className="h-10 flex-1 rounded-md border border-slate-300 px-2 text-sm" data-testid={`hw-input-${i}`} />
          <VoiceButton onText={(t) => onChange(items.map((x, j) => (j === i ? (x ? `${x} ${t}` : t) : x)))} testid={`hw-voice-${i}`} />
          {items.length > 1 && <button type="button" onClick={() => onChange(items.filter((_, j) => j !== i))} className="px-1 pt-2 text-slate-400 hover:text-red-600" aria-label="Remove item" data-testid={`hw-remove-${i}`}>✕</button>}
        </div>
      ))}
    </div>
    {items.length < 15 && <button type="button" onClick={() => onChange([...items, ""])} className="mt-1.5 rounded-full border border-dashed border-emerald-400 px-3 py-1 text-xs font-semibold text-emerald-700" data-testid="hw-add-item">+ add item</button>}
  </div>
);

export const levelOf = (students, ids) => {
  const counts = {};
  students.filter((s) => ids.includes(s.id)).forEach((s) => { counts[s.level] = (counts[s.level] || 0) + 1; });
  return Object.entries(counts).sort((a, b) => b[1] - a[1])[0]?.[0] || null;
};

const Field = ({ label, value, onChange, placeholder, testid }) => (
  <div>
    <p className="mb-1 text-xs font-bold text-slate-700">{label}</p>
    <div className="flex items-start gap-2">
      <input value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder}
        className="h-10 flex-1 rounded-md border border-slate-300 px-2 text-sm" data-testid={testid} />
      <VoiceButton onText={(t) => onChange(value ? `${value} ${t}` : t)} testid={`${testid}-voice`} />
    </div>
  </div>
);

const TestBox = ({ test, onChange }) => (
  <div className="flex flex-wrap items-center gap-2 rounded-lg bg-violet-50 p-2" data-testid="test-box">
    <input value={test.name} onChange={(e) => onChange({ ...test, name: e.target.value })} placeholder="Test name e.g. Tables 2–5"
      className="h-9 flex-1 min-w-[150px] rounded-md border border-slate-300 px-2 text-sm" data-testid="test-name" />
    {["marks", "grade"].map((m) => <button key={m} type="button" onClick={() => onChange({ ...test, mode: m })} data-testid={`test-mode-${m}`}
      className={`rounded-full px-3 py-1 text-xs font-bold ${test.mode === m ? "bg-violet-700 text-white" : "bg-white text-slate-600"}`}>{m === "marks" ? "Marks" : "Grade A–E"}</button>)}
    {test.mode === "marks" && <input type="number" min="1" value={test.out_of || ""} onChange={(e) => onChange({ ...test, out_of: Number(e.target.value) || null })}
      placeholder="Out of" className="h-9 w-20 rounded-md border border-slate-300 px-2 text-sm" data-testid="test-out-of" />}
  </div>
);

export const GroupForm = ({ g, set, students, meta, onChipsChanged }) => {
  const level = levelOf(students, g.student_ids);
  const list = level ? meta.checklists[level] : null;
  useEffect(() => {
    if (list && g.level !== level) set({ ...g, level, activities: [] });
  }, [level]); // eslint-disable-line react-hooks/exhaustive-deps
  const toggle = (a) => set({ ...g, activities: g.activities.includes(a) ? g.activities.filter((x) => x !== a) : [...g.activities, a] });
  const testOn = g.activities.includes("Daily Test") || g.activities.includes("Test");
  if (!list) return <p className="text-sm text-slate-500">Pick students first — their PTG checklist appears here.</p>;
  return (
    <div className="space-y-3" data-testid="group-form">
      <div>
        <p className="text-xs font-bold text-slate-700">PTG {list.title} checklist <span className="font-normal text-slate-500">— tap what was done today</span></p>
        <p className="mb-1.5 text-[11px] text-emerald-800" data-testid="checklist-approach">{list.approach}</p>
        <div className="flex flex-wrap gap-1.5">
          {list.items.map((a) => <button key={a} type="button" onClick={() => toggle(a)} data-testid={`act-${a}`}
            className={`rounded-full px-3 py-1 text-xs font-semibold transition-colors ${g.activities.includes(a) ? "bg-emerald-600 text-white" : "border border-slate-300 bg-white text-slate-700 hover:bg-emerald-50"}`}>{a}</button>)}
        </div>
      </div>
      {testOn && <TestBox test={g.test || { name: "", mode: "marks", out_of: null }} onChange={(test) => set({ ...g, test })} />}
      <div><p className="mb-1 text-xs font-bold text-slate-700">My chips</p>
        <ChipPicker chips={{ builtin: [], mine: meta.chips.mine }} value={g.chips} onChange={(chips) => set({ ...g, chips })} onChipsChanged={onChipsChanged} /></div>
      <Field label="Topic / what we did" value={g.topic} onChange={(topic) => set({ ...g, topic })} placeholder="e.g. Fractions Ex 5.2 · Letter B, numbers 1–20" testid="group-topic" />
      <HomeworkItems items={g.homework_items?.length ? g.homework_items : (g.homework ? g.homework.split("; ") : [""])} onChange={(homework_items) => set({ ...g, homework_items, homework: homework_items.filter((x) => x.trim()).join("; ") })} />
      {g.homework && <label className="flex items-center gap-2 text-xs text-slate-600">Due
        <input type="date" value={g.homework_due} onChange={(e) => set({ ...g, homework_due: e.target.value })} className="h-8 rounded border border-slate-300 px-1" data-testid="group-homework-due" />
        <span className="text-slate-400">(empty = next class day)</span></label>}
    </div>
  );
};

export const groupBody = (g) => ({ student_ids: g.student_ids, activities: g.activities, chips: g.chips, topic: g.topic, homework: g.homework,
  homework_items: (g.homework_items || []).map((x) => x.trim()).filter(Boolean),
  homework_due: g.homework_due, test: (g.activities.includes("Daily Test") || g.activities.includes("Test")) ? g.test : null });
